/**
 * LocationPrimingCard — Apple-compliant priming dialog for geolocation.
 *
 * Shown as a modal sheet the first time the user taps "Choose your city".
 * NEVER shown automatically. NEVER triggers the OS permission prompt
 * directly. Two equal CTAs:
 *   [Use my location]  → THEN triggers navigator.geolocation
 *   [Pick a city]      → opens CityPicker (Nominatim + bundled cities)
 *
 * This priming pattern is required by App Store Review Guideline 5.1.1
 * — apps that cold-prompt for location are auto-rejected. The text
 * explicitly states what data is used and that it stays on-device.
 *
 * The body text comes from i18n (`location_priming_*`) — must match the
 * iOS `NSLocationWhenInUseUsageDescription` string for consistency.
 */

import type { AweniContext } from '../../types';
import type { LocationService } from '../../services/LocationService';
import { CityPicker } from './CityPicker';

export interface LocationPrimingDeps {
  ctx: AweniContext;
  locationService: LocationService;
  onResolved: () => void; // called after the user makes a choice
}

export class LocationPrimingCard {
  private overlay: HTMLElement | null = null;
  private deps: LocationPrimingDeps;
  private picker: CityPicker | null = null;

  constructor(deps: LocationPrimingDeps) {
    this.deps = deps;
  }

  show(): void {
    if (this.overlay) return;
    const t = this.deps.ctx.i18n.t.bind(this.deps.ctx.i18n);
    const isRtl = this.deps.ctx.i18n.dir === 'rtl';

    const overlay = document.createElement('div');
    overlay.className = 'aweni-location-priming-overlay';
    overlay.setAttribute('role', 'dialog');
    overlay.setAttribute('aria-modal', 'true');
    overlay.setAttribute('aria-labelledby', 'aweni-priming-title');
    overlay.setAttribute('dir', isRtl ? 'rtl' : 'ltr');

    overlay.innerHTML = `
      <div class="aweni-location-priming-card">
        <div class="aweni-priming-icon" aria-hidden="true">
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round">
            <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
            <circle cx="12" cy="10" r="3"/>
          </svg>
        </div>
        <h2 id="aweni-priming-title" class="aweni-priming-title">${t('location_priming_title')}</h2>
        <p class="aweni-priming-body">${t('location_priming_body')}</p>
        <div class="aweni-priming-actions">
          <button type="button" class="aweni-priming-btn aweni-priming-primary" data-action="gps">
            ${t('location_priming_use_gps')}
          </button>
          <button type="button" class="aweni-priming-btn aweni-priming-secondary" data-action="pick">
            ${t('location_priming_pick_city')}
          </button>
          <button type="button" class="aweni-priming-skip" data-action="skip">
            ${t('location_priming_skip')}
          </button>
        </div>
      </div>
    `;

    overlay.querySelector('[data-action="gps"]')?.addEventListener('click', () => this._handleGps());
    overlay.querySelector('[data-action="pick"]')?.addEventListener('click', () => this._handlePick());
    overlay.querySelector('[data-action="skip"]')?.addEventListener('click', () => this._dismiss());
    overlay.addEventListener('click', (e) => {
      if (e.target === overlay) this._dismiss();
    });

    document.body.appendChild(overlay);
    this.overlay = overlay;
    this._injectStyles();

    // Focus the primary action for accessibility
    requestAnimationFrame(() => {
      const primary = overlay.querySelector('[data-action="gps"]') as HTMLButtonElement | null;
      primary?.focus();
    });
  }

  /** User tapped "Use my location" — NOW we trigger the OS prompt. */
  private async _handleGps(): Promise<void> {
    try {
      await this.deps.locationService.refresh();
    } catch { /* swallow — refresh resolves even on permission denial */ }
    this._dismiss();
    this.deps.onResolved();
  }

  /** User tapped "Pick a city" — open the CityPicker. */
  private _handlePick(): void {
    this._dismiss();
    if (!this.picker) {
      this.picker = new CityPicker({
        ctx: this.deps.ctx,
        locationService: this.deps.locationService,
        onResolved: () => {
          this.picker = null;
          this.deps.onResolved();
        },
      });
    }
    this.picker.show();
  }

  private _dismiss(): void {
    if (!this.overlay) return;
    this.overlay.style.opacity = '0';
    setTimeout(() => {
      this.overlay?.remove();
      this.overlay = null;
    }, 200);
  }

  private _injectStyles(): void {
    if (document.getElementById('aweni-priming-styles')) return;
    const style = document.createElement('style');
    style.id = 'aweni-priming-styles';
    style.textContent = `
      .aweni-location-priming-overlay {
        position: fixed;
        inset: 0;
        z-index: 9000;
        background: rgba(15, 23, 42, 0.7);
        display: flex;
        align-items: flex-end;
        justify-content: center;
        opacity: 1;
        transition: opacity var(--motion-duration-base) var(--motion-easing-default);
      }
      @media (min-width: 768px) {
        .aweni-location-priming-overlay { align-items: center; }
      }
      .aweni-location-priming-card {
        background: var(--card-bg, #1E293B);
        color: var(--text-primary, #F1F5F9);
        border-radius: 24px 24px 0 0;
        padding: 28px 24px 24px;
        width: 100%;
        max-width: 480px;
        box-shadow: 0 -10px 40px rgba(0,0,0,0.4);
      }
      @media (min-width: 768px) {
        .aweni-location-priming-card { border-radius: 24px; }
      }
      .aweni-priming-icon {
        width: 64px;
        height: 64px;
        border-radius: 50%;
        background: rgba(10, 10, 10,0.15);
        color: var(--accent, #0A0A0A);
        display: flex;
        align-items: center;
        justify-content: center;
        margin: 0 auto 16px;
      }
      .aweni-priming-title {
        font-size: 20px;
        font-weight: 800;
        text-align: center;
        margin: 0 0 12px;
      }
      .aweni-priming-body {
        font-size: 14px;
        line-height: 1.5;
        text-align: center;
        color: var(--text-secondary, #94A3B8);
        margin: 0 0 24px;
      }
      .aweni-priming-actions {
        display: flex;
        flex-direction: column;
        gap: 10px;
      }
      .aweni-priming-btn {
        padding: 14px 20px;
        border-radius: 14px;
        font-size: 15px;
        font-weight: 700;
        border: none;
        cursor: pointer;
        min-height: 48px;
        transition: transform var(--motion-duration-fast) var(--motion-easing-default);
      }
      .aweni-priming-btn:active { transform: scale(0.97); }
      .aweni-priming-primary { background: var(--accent, #0A0A0A); color: #fff; }
      .aweni-priming-secondary {
        background: transparent;
        color: var(--text-primary, #F1F5F9);
        border: 1px solid var(--border, rgba(255,255,255,0.15));
      }
      .aweni-priming-skip {
        background: transparent;
        color: var(--text-secondary, #94A3B8);
        font-size: 13px;
        font-weight: 500;
        border: none;
        padding: 8px;
        margin-top: 4px;
        cursor: pointer;
        min-height: 44px;
      }
    `;
    document.head.appendChild(style);
  }
}
